export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = Exclude<ThemePreference, "system">;

// Shared with the Simate website and Sinfra on the same origin and tab.
export const themeStorageKey = "simate-theme-override";

const themeColor = {
  light: "#f8fafc",
  dark: "#181a1e"
} satisfies Record<ResolvedTheme, string>;

export const isThemePreference = (value: string | null): value is ThemePreference => (
  value === "system" || value === "light" || value === "dark"
);

export const readThemePreference = (storage?: Pick<Storage, "getItem">): ThemePreference => {
  if (!storage) return "system";
  try {
    const stored = storage.getItem(themeStorageKey);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
};

export const writeThemePreference = (
  preference: ThemePreference,
  storage?: Pick<Storage, "setItem">
) => {
  if (!storage) return;
  try {
    storage.setItem(themeStorageKey, preference);
  } catch {
    // Theme switching remains usable when storage is disabled or full.
  }
};

export const resolveTheme = (
  preference: ThemePreference,
  systemPrefersLight = false
): ResolvedTheme => preference === "system"
  ? (systemPrefersLight ? "light" : "dark")
  : preference;

export const systemPrefersLight = () => typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(prefers-color-scheme: light)").matches;

export const applyResolvedTheme = (theme: ResolvedTheme, target?: Document) => {
  const documentTarget = target ?? (typeof document === "undefined" ? undefined : document);
  if (!documentTarget) return;
  documentTarget.documentElement.dataset.theme = theme;
  documentTarget.documentElement.style.colorScheme = theme;
  documentTarget.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", themeColor[theme]);
};

const cssColor = (styles: CSSStyleDeclaration, variable: string, fallback: string) => (
  styles.getPropertyValue(variable).trim() || fallback
);

export const readRenderedThemePalette = (target?: Element) => {
  const root = target ?? document.documentElement;
  const styles = getComputedStyle(root);
  return {
    canvas: cssColor(styles, "--color-canvas-default", "#181a1e"),
    canvasOverlay: cssColor(styles, "--color-canvas-overlay", "#1d2024"),
    canvasInset: cssColor(styles, "--color-canvas-inset", "#131519"),
    border: cssColor(styles, "--color-border-default", "rgba(236, 239, 244, .12)"),
    borderMuted: cssColor(styles, "--color-border-muted", "rgba(236, 239, 244, .075)"),
    foreground: cssColor(styles, "--color-fg-default", "#eceff3"),
    muted: cssColor(styles, "--color-fg-muted", "rgba(236, 239, 243, .64)"),
    subtle: cssColor(styles, "--color-fg-subtle", "rgba(236, 239, 243, .43)"),
    neutralMuted: cssColor(styles, "--color-neutral-muted", "rgba(236, 239, 243, .1)"),
    accent: cssColor(styles, "--color-accent-fg", "#88c0d0"),
    accentSubtle: cssColor(styles, "--color-accent-subtle", "rgba(136, 192, 208, .09)"),
    success: cssColor(styles, "--color-success-fg", "#70b489"),
    attention: cssColor(styles, "--color-attention-fg", "#f1b467"),
    danger: cssColor(styles, "--color-danger-fg", "#fc6b83"),
    done: cssColor(styles, "--color-done-fg", "#b48ead"),
    ansi: {
      black: cssColor(styles, "--color-ansi-black", "#25282d"),
      blackBright: cssColor(styles, "--color-ansi-black-bright", "rgba(236, 239, 243, .62)"),
      red: cssColor(styles, "--color-ansi-red", "#fc6b83"),
      redBright: cssColor(styles, "--color-ansi-red-bright", "#fc6b83"),
      green: cssColor(styles, "--color-ansi-green", "#3fa266"),
      greenBright: cssColor(styles, "--color-ansi-green-bright", "#70b489"),
      yellow: cssColor(styles, "--color-ansi-yellow", "#d2943e"),
      yellowBright: cssColor(styles, "--color-ansi-yellow-bright", "#f1b467"),
      blue: cssColor(styles, "--color-ansi-blue", "#81a1c1"),
      blueBright: cssColor(styles, "--color-ansi-blue-bright", "#87a6c4"),
      magenta: cssColor(styles, "--color-ansi-magenta", "#b48ead"),
      magentaBright: cssColor(styles, "--color-ansi-magenta-bright", "#b48ead"),
      cyan: cssColor(styles, "--color-ansi-cyan", "#88c0d0"),
      cyanBright: cssColor(styles, "--color-ansi-cyan-bright", "#88c0d0"),
      white: cssColor(styles, "--color-ansi-white", "#eceff3"),
      whiteBright: cssColor(styles, "--color-ansi-white-bright", "#ffffff")
    }
  };
};

export type ThemePalette = ReturnType<typeof readRenderedThemePalette>;
